<template>
  <CustomDrawer
    :is-open="isOpen"
    position="right"
    width="min(1400px, 94vw)"
    height="100vh"
    drawer-class="logs-actions-drawer"
    @update:is-open="handleDrawerToggle"
    @close="closeDrawer"
  >
    <div class="logs-actions">
      <div class="logs-actions__header">
        <div>
          <h2 class="logs-actions__title">
            {{ t('logsActions.title', { serviceName: serviceNameLabel }) }}
          </h2>
        </div>

        <Button
          shape="circle"
          icon="close-outline"
          :aria-label="t('logsActions.closeButtonAriaLabel')"
          @click="closeDrawer"
        />
      </div>

      <section class="logs-actions__panel">
        <div class="logs-actions__section-title">
          {{ t('logsActions.filtersTitle') }}
        </div>

        <EMFormRow class="logs-actions__row">
          <EMFieldGroup :label="t('logsActions.usernameLabel')">
            <EMInputTextForm
              v-model="username"
              type="text"
              :placeholder="t('logsActions.usernamePlaceholder')"
              clearInput
              @keyup.enter="search"
            />
          </EMFieldGroup>

          <EMFieldGroup :label="t('logsActions.fullnameLabel')">
            <EMInputTextForm
              v-model="fullname"
              type="text"
              :placeholder="t('logsActions.fullnamePlaceholder')"
              clearInput
              @keyup.enter="search"
            />
          </EMFieldGroup>
        </EMFormRow>

        <EMFormRow class="logs-actions__row">
          <div class="logs-actions__row logs-actions__row--period">
            <EMFieldGroup :label="t('logsActions.fromLabel')">
              <EMInputTextForm v-model="from" type="date" />
            </EMFieldGroup>
            <EMFieldGroup :label="t('logsActions.toLabel')">
              <EMInputTextForm v-model="to" type="date" />
            </EMFieldGroup>
          </div>

          <EMFieldGroup :label="t('logsActions.minStatusCodeLabel')">
            <EMInputTextForm
              v-model="minStatusCodeInput"
              type="number"
              number-controls-background-color="var(--ion-input-background)"
              number-controls-color-scheme="light"
              :placeholder="t('logsActions.minStatusCodePlaceholder')"
            />
          </EMFieldGroup>

          <EMFieldGroup :label="t('logsActions.maxStatusCodeLabel')">
            <EMInputTextForm
              v-model="maxStatusCodeInput"
              type="number"
              number-controls-background-color="var(--ion-input-background)"
              number-controls-color-scheme="light"
              :placeholder="t('logsActions.maxStatusCodePlaceholder')"
            />
          </EMFieldGroup>
          <EMFieldGroup :label="t('logsActions.topLabel')">
            <EMInputTextForm
              v-model="topInput"
              type="number"
              number-controls-background-color="var(--ion-input-background)"
              number-controls-color-scheme="light"
              :placeholder="t('logsActions.topPlaceholder')"
            />
          </EMFieldGroup>
        </EMFormRow>

        <p v-if="validationMessage" class="logs-actions__error">
          {{ validationMessage }}
        </p>

        <div class="logs-actions__actions">
          <Button type="primary-gradient" :loading="isLoading" @click="search">
            {{ t('logsActions.searchButton') }}
          </Button>
          <Button @click="resetFilters">
            {{ t('logsActions.resetButton') }}
          </Button>
        </div>
      </section>

      <section class="logs-actions__panel">
        <div class="logs-actions__section-header">
          <div>
            <div class="logs-actions__section-title">
              {{ t('logsActions.resultsTitle') }}
            </div>
          </div>
          <span v-if="hasSearched" class="logs-actions__counter">
            {{ t('logsActions.resultsCount', { count: requestLogs.length }) }}
          </span>
        </div>

        <div v-if="!hasSearched" class="logs-actions__empty-state">
          {{ t('logsActions.emptyState') }}
        </div>

        <template v-else>
          <p v-if="errorMessage" class="logs-actions__error">
            {{ errorMessage }}
          </p>
          <EMTableWithCard
            v-if="requestLogs.length > 0"
            :data="requestLogs"
            :columns="requestLogsColumns"
            :searchable="true"
            :selectable="false"
            :page-size="0"
            :loading="isLoading"
            :emptyMessage="t('logsActions.noResults')"
            class="logs-actions__table"
          >
            <template #cell-timestamp="{ value }">
              {{ formatDateTime(String(value)) }}
            </template>
            <template #cell-durationMs="{ value }">
              {{ formatDuration(value) }}
            </template>
            <template #cell-method="{ value }">
              <span
                class="logs-actions__method-badge"
                :class="`logs-actions__method-badge--${getMethodBadgeVariant(value)}`"
              >
                {{ formatMethod(value) }}
              </span>
            </template>
            <template #cell-requestBody="{ value }">
              <RequestBodyCell :value="value" />
            </template>
          </EMTableWithCard>
          <div v-else class="logs-actions__empty-state">
            {{ t('logsActions.noResults') }}
          </div>
        </template>
      </section>
    </div>
  </CustomDrawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useLocale } from '@/common/composables/useLocale';
import { formatDateTime } from '@/common/utils/dateFormatter';
import {
  Button,
  CustomDrawer,
  EMFieldGroup,
  EMFormRow,
  EMInputTextForm,
  EMTableWithCard,
} from '@/common/components/ui';
import { logger } from '@/common/services/config';
import { LogsActionsServiceName } from '@/common/constants';
import { logsActionsService } from '../services/logsActionsService';
import RequestBodyCell from './RequestBodyCell.vue';
import type { ColumnDefinition } from '@/common/components/ui/table/types';
import type { RequestLogEntry } from '../types';

const props = withDefaults(
  defineProps<{
    isOpen: boolean;
    serviceName: LogsActionsServiceName;
    initialUsername?: string;
    initialFullname?: string;
    initialFrom?: string;
    initialTo?: string;
    initialMinStatusCode?: number | null;
    initialMaxStatusCode?: number | null;
    initialTop?: number;
  }>(),
  {
    initialUsername: '',
    initialFullname: '',
    initialFrom: '',
    initialTo: '',
    initialMinStatusCode: null,
    initialMaxStatusCode: null,
    initialTop: 100,
  }
);

const emit = defineEmits<{
  'update:is-open': [value: boolean];
  close: [];
}>();

const { t } = useLocale();

const username = ref('');
const fullname = ref('');
const from = ref('');
const to = ref('');
const minStatusCode = ref<number | null>(null);
const maxStatusCode = ref<number | null>(null);
const top = ref(100);
const isLoading = ref(false);
const hasSearched = ref(false);
const requestLogs = ref<RequestLogEntry[]>([]);
const errorMessage = ref('');
const validationMessage = ref('');

const serviceNameLabel = computed(() => {
  const translationKey = `logsActions.serviceNames.${props.serviceName}`;
  const translatedLabel = t(translationKey);
  return translatedLabel === translationKey ? props.serviceName : translatedLabel;
});

const requestLogsColumns = computed<ColumnDefinition<RequestLogEntry>[]>(() => [
  {
    key: 'timestamp',
    label: t('logsActions.columns.timestamp'),
    width: '180px',
    formatter: (value) => formatDateTime(String(value)),
  },
  { key: 'method', label: t('logsActions.columns.method'), width: '100px' },
  { key: 'path', label: t('logsActions.columns.path'), minWidth: '220px' },
  { key: 'query', label: t('logsActions.columns.query'), minWidth: '180px' },
  { key: 'statusCode', label: t('logsActions.columns.statusCode'), width: '120px' },
  { key: 'durationMs', label: t('logsActions.columns.durationMs'), width: '130px' },
  { key: 'requestBody', label: t('logsActions.columns.requestBody'), minWidth: '420px' },
]);

const minStatusCodeInput = computed<string | number>({
  get: () => (minStatusCode.value === null ? '' : minStatusCode.value),
  set: (value) => {
    minStatusCode.value = toNullableNumber(value);
  },
});

const maxStatusCodeInput = computed<string | number>({
  get: () => (maxStatusCode.value === null ? '' : maxStatusCode.value),
  set: (value) => {
    maxStatusCode.value = toNullableNumber(value);
  },
});

const topInput = computed<string | number>({
  get: () => top.value,
  set: (value) => {
    const parsed = toNullableNumber(value);
    top.value = parsed === null ? 100 : Math.max(1, parsed);
  },
});

const toNullableNumber = (value: string | number | null | undefined): number | null => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsedValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsedValue) ? Math.trunc(parsedValue) : null;
};

const resetState = () => {
  username.value = props.initialUsername;
  fullname.value = props.initialFullname;
  from.value = props.initialFrom;
  to.value = props.initialTo;
  minStatusCode.value = props.initialMinStatusCode;
  maxStatusCode.value = props.initialMaxStatusCode;
  top.value = props.initialTop;
  requestLogs.value = [];
  errorMessage.value = '';
  validationMessage.value = '';
  hasSearched.value = false;
  isLoading.value = false;
};

const handleDrawerToggle = (value: boolean) => {
  emit('update:is-open', value);
};

const closeDrawer = () => {
  emit('update:is-open', false);
  emit('close');
};

const isValidDate = (value: string): boolean => {
  if (!value) return true;
  const timestamp = new Date(value).getTime();
  return !Number.isNaN(timestamp);
};

const toUtcDayStart = (value: string): string => `${value}T00:00:00.000Z`;
const toUtcDayEnd = (value: string): string => `${value}T23:59:59.999Z`;

const validateFilters = (): boolean => {
  const hasIdentity = Boolean(username.value.trim() || fullname.value.trim());

  if (!hasIdentity) {
    validationMessage.value = t('logsActions.validation.missingIdentity');
    return false;
  }

  if (!isValidDate(from.value) || !isValidDate(to.value)) {
    validationMessage.value = t('logsActions.validation.invalidDate');
    return false;
  }

  if (from.value && to.value && new Date(from.value) > new Date(to.value)) {
    validationMessage.value = t('logsActions.validation.invalidDateRange');
    return false;
  }

  if (
    minStatusCode.value !== null &&
    maxStatusCode.value !== null &&
    minStatusCode.value > maxStatusCode.value
  ) {
    validationMessage.value = t('logsActions.validation.invalidStatusRange');
    return false;
  }

  validationMessage.value = '';
  return true;
};

const search = async () => {
  if (!validateFilters()) {
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';

  try {
    requestLogs.value = await logsActionsService.getRequestLogs({
      serviceName: props.serviceName,
      username: username.value.trim() || undefined,
      fullname: fullname.value.trim() || undefined,
      from: from.value ? toUtcDayStart(from.value) : undefined,
      to: to.value ? toUtcDayEnd(to.value) : undefined,
      minStatusCode: minStatusCode.value ?? undefined,
      maxStatusCode: maxStatusCode.value ?? undefined,
      top: top.value,
    });
    hasSearched.value = true;
  } catch (error) {
    logger.error('Failed to fetch request logs:', error);
    errorMessage.value = error instanceof Error ? error.message : t('logsActions.errors.generic');
    requestLogs.value = [];
    hasSearched.value = true;
  } finally {
    isLoading.value = false;
  }
};

const resetFilters = () => {
  resetState();
};

const formatDuration = (value: unknown): string => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) return '-';
  return `${Math.round(numericValue)} ms`;
};

const normalizeMethod = (value: unknown): string =>
  String(value ?? '')
    .trim()
    .toUpperCase();

const formatMethod = (value: unknown): string => {
  const normalizedMethod = normalizeMethod(value);
  return normalizedMethod || '-';
};

const getMethodBadgeVariant = (value: unknown): 'get' | 'post' | 'put' | 'delete' | 'default' => {
  const normalizedMethod = normalizeMethod(value);
  if (normalizedMethod === 'GET') return 'get';
  if (normalizedMethod === 'POST') return 'post';
  if (normalizedMethod === 'PUT') return 'put';
  if (normalizedMethod === 'DELETE') return 'delete';
  return 'default';
};

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      resetState();
    }
  },
  { immediate: true }
);
</script>

<style scoped>
.logs-actions {
  --ion-method-get: #294bb1;
  --ion-method-post: #0daf4b;
  --ion-method-put: #f4c436;
  --ion-method-delete: #c8303a;

  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px;
}

.logs-actions__header,
.logs-actions__section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.logs-actions__title {
  margin: 0;
  font-size: 24px;
  color: var(--ion-text-color);
}

.logs-actions__subtitle,
.logs-actions__section-description {
  margin: 6px 0 0;
  color: var(--ion-text-color-secondary, #6b7280);
}

.logs-actions__panel {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid var(--ion-input-border-color, rgba(0, 0, 0, 0.08));
  border-radius: 20px;
  background: var(--ion-card-background, #fff);
}

.logs-actions__section-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--ion-text-color);
}

.logs-actions__row {
  gap: 16px;
}

.logs-actions__row--period {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-right: 16px;
}

.logs-actions__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
}

.logs-actions__error {
  margin: 0;
  color: var(--ion-color-danger, #eb445a);
  font-size: 14px;
}

.logs-actions__empty-state {
  padding: 20px;
  border: 1px dashed var(--ion-input-border-color, rgba(0, 0, 0, 0.12));
  border-radius: 18px;
  color: var(--ion-text-color-secondary, #6b7280);
}

.logs-actions__counter {
  font-size: 13px;
  color: var(--ion-text-color-secondary, #6b7280);
  white-space: nowrap;
}

.logs-actions__table {
  width: 100%;
}

.logs-actions__method-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
  color: #ffffff;
  background: var(--ion-color-medium, #6b7280);
}

.logs-actions__method-badge--get {
  background: var(--ion-method-get);
}

.logs-actions__method-badge--post {
  background: var(--ion-method-post);
}

.logs-actions__method-badge--put {
  background: var(--ion-method-put);
  color: #1f2937;
}

.logs-actions__method-badge--delete {
  background: var(--ion-method-delete);
}

@media (max-width: 768px) {
  .logs-actions__row--single {
    max-width: 100%;
  }

  .logs-actions {
    padding: 16px;
  }
}
</style>
